import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export const useQueryParams = (names: string[], defaultValue?: Record<string, any>) => {
	const [urlSearchParams, setURLSearchParams] = useSearchParams();
	const location = useLocation();
	const navigate = useNavigate();

	const deleteValue = (name: string, value?: string) =>
		updateSearchParams(l => {
			l.delete(name, value);
			return l;
		});

	const setValues = (values: Record<string, any>) =>
		updateSearchParams(l => {
			Object.keys(values).forEach(key => {
				const value = values[key];
				if (value === undefined || value === null) {
					l.delete(key);
				} else {
					l.set(key, value);
				}
			});

			return l;
		});

	const appendValue = (name: string, value: any) =>
		updateSearchParams(l => {
			l.append(name, value);
			return l;
		});

	const setValue = (name: string, value: any) =>
		updateSearchParams(l => {
			if (value === undefined || value === null) {
				l.delete(name);
			} else {
				l.set(name, value);
			}
			return l;
		});

	const updateSearchParams = (
		callback: (urlSearchParams: URLSearchParams) => URLSearchParams,
		init?: string[][] | Record<string, string> | string | URLSearchParams,
	) => {
		const urlSearchParams = callback(new URLSearchParams(init ?? location.search));
		navigate(
			{
				search: urlSearchParams.toString(),
				pathname: location.pathname,
			},
			{ replace: true },
		);
	};

	const clearValues = () => {
		updateSearchParams(l => l, {});
	};

	const getValues = () => {
		const urlSearchParams = new URLSearchParams(location.search);
		const values = [];

		for (let index = 0; index < names.length; index++) {
			const name = names[index];
			const nameValues = urlSearchParams.getAll(name);
			let value = defaultValue?.[name];

			if (nameValues.length === 1) {
				value = nameValues[0];
			} else if (nameValues.length > 1) {
				value = nameValues;
			}

			values.push(value);
		}

		return values;
	};

	return {
		values: getValues(),
		setURLSearchParams,
		updateSearchParams,
		urlSearchParams,
		appendValue,
		deleteValue,
		clearValues,
		setValues,
		setValue,
	};
};
